import { useEffect, useState, useCallback, useRef } from 'react';

import { useSelector, useDispatch } from 'react-redux';
import { updateRestorationState, addCleanUpId } from 'actions/backRestoration';
import { useHistory, useLocation } from 'react-router-dom';
import { MemoryHistory } from 'history';
import { VideoCtvRedux } from '../store-types';
import { GridState } from 'app-types';

export type RestorationState = GridState | null;

export const useRestoration = (restorationId: string | null = null, shouldCleanUp = false) => {
  const history = useHistory() as MemoryHistory;
  const dispatch = useDispatch();

  const { state: locationState } = useLocation<{ restore?: boolean }>();
  const shouldProvideRestoration = useRef(
    history.action === 'POP' || !!locationState?.restore,
  );

  const backRestoration = useSelector(
    (reduxState: VideoCtvRedux) => reduxState?.backRestoration?.restorationState?.[restorationId as string],
  );

  const restorationState = shouldProvideRestoration.current
    ? (backRestoration ?? null)
    : null;

  const [tempRestorationState, setTempRestorationState] = useState<RestorationState|null>(
    restorationState,
  );

  const latestRestorationRef = useRef<RestorationState|null>(null);

  useEffect(() => {
    if (shouldCleanUp) {
      dispatch(addCleanUpId(restorationId));
    }
  }, [dispatch, restorationId, shouldCleanUp]);

  useEffect(() => {
    latestRestorationRef.current = tempRestorationState;
  }, [tempRestorationState]);

  useEffect(() => {
    return () => {
      if (restorationId && latestRestorationRef.current) {
        dispatch(
          updateRestorationState(restorationId, latestRestorationRef.current),
        );
      }
    };
  }, [restorationId, dispatch]);

  const setRestorationState = useCallback((state: RestorationState) => {
    setTempRestorationState(state);
    latestRestorationRef.current = state;
  }, []);

  return {
    setRestorationState,
    restorationState,
  };
};
